import { getGetChannelsApiV1QueryKey } from "@/api/__generated__/api.schema";
import { addYoutubeChannelApiV1 } from "@/api/__generated__/api.schema";
import { KnownError } from "@/api/client";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { stripNil } from "@/utils/strip-nil";
import { useToastMessage } from "@backpac/monica";
import { useGoogleLogin } from "@react-oauth/google";
import { useQueryClient } from "@tanstack/react-query";

type Props = {
  onSuccess?: (channelId: string) => void;
};

export function useRegisterChannelViaGoogleOAuth({ onSuccess }: Props) {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  const { error: errorToast, info } = useToastMessage();

  const registerYoutubeChannel = useGoogleLogin({
    ux_mode: "popup",
    flow: "auth-code",
    select_account: true,
    // This is to avoid the screen where user can select the granular permissions. It gets implict agreement to the permissions.
    // REF: https://developers.googleblog.com/en/more-granular-google-account-permissions-with-google-oauth-and-apis/
    enable_serial_consent: false,
    // TODO: If we need to get analytics data, we should add https://www.googleapis.com/auth/yt-analytics.readonly
    scope: "email https://www.googleapis.com/auth/youtube.readonly",
    onSuccess: async (response) => {
      const { code } = response;
      try {
        const { data } = await addYoutubeChannelApiV1(workspaceId, {
          googleOAuthCode: code,
        });

        await queryClient.refetchQueries({
          queryKey: [...getGetChannelsApiV1QueryKey(workspaceId)],
        });
        info("Channel added successfully");
        onSuccess?.(stripNil(data).item.channel.id);
      } catch (error) {
        if (error instanceof KnownError) {
          if (error.status === 403) {
            errorToast("This channel has already been added.");
          }

          if (error.status === 404) {
            errorToast("Channel not found");
          }
          if (error.status === 409) {
            errorToast("This channel is already added to another account.");
          }
          if (error.status === 422) {
            errorToast("Failed to determine channel to fetch. Please contact us");
          }

          if (error.status >= 500) {
            errorToast("Please try again. If the problem persists, please contact us.");
          }
        } else {
          throw error;
        }
      }
    },
    onError: (error) => {
      console.log("Login Failed", error);
    },
  });
  return registerYoutubeChannel;
}
